import { useEffect, useMemo, useState } from "react";

import { downloadVerifiedCsv } from "../utils/exportCsv";
import { C, font } from "../styles/tokens";

function fmtIsoDay(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Bank-statement-style export dialog: pick a from-to range and download all
 * verified credentials decided within it as an Excel-compatible CSV.
 *
 * Props:
 *   open       -- boolean, controls visibility
 *   onClose    -- called when the user cancels or after a successful download
 *   items      -- the full verification-request array (any status); we filter
 *                to VERIFIED + within range
 *   orgName    -- optional, used in the filename
 */
export function ExportRangeDialog({ open, onClose, items, orgName }) {
  const today = useMemo(() => fmtIsoDay(new Date()), []);
  const monthAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return fmtIsoDay(d);
  }, []);

  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFrom(monthAgo);
    setTo(today);
    setError(null);
  }, [open, monthAgo, today]);

  if (!open) return null;

  const verifiedInRange = (items ?? [])
    .filter((r) => r.status === "VERIFIED")
    .filter((r) => {
      if (!r.decidedAt) return false;
      const t = new Date(r.decidedAt).getTime();
      if (from && t < new Date(`${from}T00:00:00`).getTime()) return false;
      if (to && t > new Date(`${to}T23:59:59.999`).getTime()) return false;
      return true;
    }).length;

  function handleDownload() {
    if (!from || !to) {
      setError("Pick both a From and a To date.");
      return;
    }
    if (from > to) {
      setError("'From' must be on or before 'To'.");
      return;
    }
    const n = downloadVerifiedCsv(items, orgName, { from, to });
    if (n === 0) {
      setError("No verified credentials in this date range.");
      return;
    }
    onClose?.();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: C.bg,
          borderRadius: 10,
          boxShadow: "0 16px 48px rgba(0,0,0,0.22)",
          fontFamily: font,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "16px 18px 8px" }}>
          <h2
            style={{
              fontSize: 16,
              fontWeight: 700,
              margin: "0 0 4px",
              color: C.textDark,
            }}
          >
            Download verified credentials
          </h2>
          <p
            style={{
              fontSize: 13,
              color: C.textMed,
              margin: 0,
              lineHeight: 1.45,
            }}
          >
            Pick a date range, as you would for a bank statement. We'll export
            every verified credential decided within those dates as an
            Excel-compatible CSV.
          </p>
        </div>

        <div
          style={{
            padding: "8px 18px 14px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
        >
          <DateField
            label="From"
            value={from}
            max={to || today}
            onChange={setFrom}
          />
          <DateField
            label="To"
            value={to}
            min={from}
            max={today}
            onChange={setTo}
          />
        </div>

        <div style={{ padding: "0 18px 4px", fontSize: 12, color: C.textMed }}>
          {verifiedInRange === 0
            ? "No verified credentials match this range yet."
            : `${verifiedInRange} verified credential${verifiedInRange === 1 ? "" : "s"} will be exported.`}
        </div>

        {error && (
          <div
            style={{
              margin: "8px 18px 0",
              fontSize: 12,
              color: C.red,
              background: C.redLight,
              padding: "6px 10px",
              borderRadius: 4,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "14px 18px",
            marginTop: 12,
            borderTop: `1px solid ${C.divider}`,
            background: C.bgMuted,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: C.textDark,
              background: "transparent",
              border: `1px solid ${C.dividerStrong}`,
              borderRadius: 6,
              cursor: "pointer",
              fontFamily: font,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={verifiedInRange === 0}
            style={{
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: "white",
              background: verifiedInRange === 0 ? C.textLight : C.blue,
              border: 0,
              borderRadius: 6,
              cursor: verifiedInRange === 0 ? "default" : "pointer",
              fontFamily: font,
            }}
          >
            Download Excel
          </button>
        </div>
      </div>
    </div>
  );
}

function DateField({ label, value, onChange, min, max }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: C.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <input
        type="date"
        value={value}
        min={min || undefined}
        max={max || undefined}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 10px",
          fontSize: 13,
          fontFamily: font,
          border: `1.5px solid ${C.dividerStrong}`,
          borderRadius: 6,
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </label>
  );
}
